import { useState, useEffect, createContext, useContext } from "react";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rajib_admin_token";

const AdminCtx = createContext({ token: "", headers: {}, signOut: () => {} });
export const useAdmin = () => useContext(AdminCtx);

export default function AdminGate({ children, title = "Admin sign-in" }) {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [draft, setDraft] = useState("");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(!!token);
  const [error, setError] = useState("");

  const verify = async (t) => {
    if (!t) {
      setAuthed(false);
      setChecking(false);
      return;
    }
    setChecking(true);
    setError("");
    try {
      await axios.get(`${API}/admin/whoami`, { headers: { "X-Admin-Token": t } });
      setToken(t);
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, t);
    } catch (e) {
      if (e?.response?.status === 401) {
        setError("Incorrect admin token.");
      } else {
        setError("Could not connect. Try again.");
      }
      setAuthed(false);
      localStorage.removeItem(TOKEN_KEY);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (token) verify(token);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
  };

  if (checking) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <p className="text-[#5C5852] italic">Verifying…</p>
      </div>
    );
  }

  if (!authed) {
    return (
      <div
        className="min-h-[70vh] flex items-center justify-center px-6 py-20"
        data-testid="admin-login"
      >
        <motion.form
          onSubmit={(e) => {
            e.preventDefault();
            verify(draft);
          }}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md rounded-sm bg-[#F2EFEB] border border-[#D8D3CC] p-7 sm:p-9"
        >
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#4A6B53]/10 text-[#4A6B53] mb-4">
            <Lock size={22} />
          </div>
          <h1 className="font-serif text-3xl text-[#2C2A28]">{title}</h1>
          <p className="mt-2 text-sm text-[#5C5852]">
            Enter your admin token to continue.
          </p>
          <input
            type="password"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Admin token"
            className="w-full mt-5 bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-4 py-3 text-[#2C2A28] field-ring"
            data-testid="admin-token-input"
            autoFocus
          />
          {error && (
            <p className="mt-3 text-xs text-[#CC7A52]" data-testid="admin-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            className="mt-5 w-full px-4 py-3 rounded-sm bg-[#4A6B53] text-[#FAF9F6] hover:bg-[#3A5441] transition-colors text-sm tracking-wide"
            data-testid="admin-login-submit"
          >
            Sign in
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <AdminCtx.Provider
      value={{ token, headers: { "X-Admin-Token": token }, signOut }}
    >
      {children}
    </AdminCtx.Provider>
  );
}
